import {validatePrivateMediaImage} from '../platform/private-media-storage.js';

export const JournalPhotoStatus=Object.freeze({
  PENDING_UPLOAD:'pending_upload',
  UPLOAD_FAILED:'upload_failed',
  PENDING_METADATA:'pending_metadata',
  METADATA_FAILED:'metadata_failed',
  UPLOADED:'uploaded',
  DELETE_FAILED:'delete_failed'
});

const STATUSES=new Set(Object.values(JournalPhotoStatus));

function segment(value,label){
  const text=String(value||'').trim();
  if(!/^[A-Za-z0-9_-]{1,128}$/.test(text))throw new TypeError('Invalid '+label);
  return text;
}

function optionalText(value,max=1024){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(!text)return null;
  if(text.length>max)throw new TypeError('Text value is too long');
  return text;
}

function optionalTimestamp(value,label){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function status(value){
  const key=String(value||JournalPhotoStatus.PENDING_UPLOAD).trim().toLowerCase();
  if(!STATUSES.has(key))throw new TypeError('Unsupported journal photo status');
  return key;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return segment(id,'photoId');
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('photo id is required when crypto.randomUUID is unavailable');
}

export function buildJournalPhotoPath({
  userId,
  sessionId,
  itemId=null,
  photoId,
  mimeType,
  sizeBytes
}={}){
  const image=validatePrivateMediaImage({mimeType,sizeBytes});
  const user=segment(userId,'userId');
  const session=segment(sessionId,'sessionId');
  const id=segment(photoId,'photoId');
  const scope=itemId
    ?'items/'+segment(itemId,'itemId')
    :'session';
  return user+'/journal/'+session+'/'+scope+'/'+id+'.'+image.extension;
}

export function isOwnedJournalPhotoPath(path,{userId,sessionId,itemId=null,photoId=null}={}){
  const value=String(path||'').trim();
  if(!value)return false;
  const prefix=itemId
    ?segment(userId,'userId')+'/journal/'+segment(sessionId,'sessionId')+'/items/'+segment(itemId,'itemId')+'/'
    :segment(userId,'userId')+'/journal/'+segment(sessionId,'sessionId')+'/session/';
  if(!value.startsWith(prefix))return false;
  if(photoId&&!value.slice(prefix.length).startsWith(segment(photoId,'photoId')+'.'))return false;
  return true;
}

export function createJournalPhoto({
  id,
  userId,
  sessionId,
  stageId=null,
  itemId=null,
  capturedAt=null,
  mimeType,
  sizeBytes,
  localPreviewUri=null,
  storagePath=null,
  photoStatus=JournalPhotoStatus.PENDING_UPLOAD,
  errorCode=null
}={}){
  const photoId=idOrGenerated(id);
  const image=validatePrivateMediaImage({mimeType,sizeBytes});
  const user=segment(userId,'userId');
  const session=segment(sessionId,'sessionId');
  const stage=stageId==null||stageId===''?null:segment(stageId,'stageId');
  const item=itemId==null||itemId===''?null:segment(itemId,'itemId');
  const path=optionalText(storagePath,1024);
  if(path&&!isOwnedJournalPhotoPath(path,{userId:user,sessionId:session,itemId:item,photoId})){
    throw new TypeError('Journal photo path is outside the expected scope');
  }
  const state=status(photoStatus);
  if(state===JournalPhotoStatus.UPLOADED&&!path){
    throw new TypeError('Uploaded journal photo requires storagePath');
  }
  return Object.freeze({
    id:photoId,
    userId:user,
    sessionId:session,
    stageId:stage,
    itemId:item,
    capturedAt:optionalTimestamp(capturedAt,'capturedAt'),
    mimeType:image.mimeType,
    sizeBytes:image.sizeBytes,
    localPreviewUri:optionalText(localPreviewUri,2048),
    storagePath:path,
    photoStatus:state,
    errorCode:optionalText(errorCode,80)
  });
}

export function journalPhotoPath(photo){
  const value=createJournalPhoto(photo);
  return buildJournalPhotoPath({
    userId:value.userId,
    sessionId:value.sessionId,
    itemId:value.itemId,
    photoId:value.id,
    mimeType:value.mimeType,
    sizeBytes:value.sizeBytes
  });
}

export function updateJournalPhotoState(photo,{
  photoStatus,
  storagePath=photo?.storagePath??null,
  errorCode=null
}={}){
  const current=createJournalPhoto(photo);
  return createJournalPhoto({
    ...current,
    id:current.id,
    userId:current.userId,
    sessionId:current.sessionId,
    stageId:current.stageId,
    itemId:current.itemId,
    capturedAt:current.capturedAt,
    storagePath,
    photoStatus:photoStatus??current.photoStatus,
    errorCode
  });
}

export function journalPhotoToRow(photo){
  const value=createJournalPhoto(photo);
  if(!value.storagePath)throw new TypeError('storagePath is required for server media metadata');
  if(!value.capturedAt)throw new TypeError('capturedAt is required for server media metadata');
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    stage_id:value.stageId,
    item_id:value.itemId,
    captured_at:value.capturedAt,
    storage_path:value.storagePath,
    mime_type:value.mimeType,
    size_bytes:value.sizeBytes
  });
}
